import React, { useState, useRef } from 'react';
import HubPageLayout from '../components/HubPageLayout';
import FramedPanel from '../components/FramedPanel';
import { API_BASE_URL, fetchWithCsrf } from '../../../config/api';

const ImageCheckPage = () => {
  const [imageUrl, setImageUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [matches, setMatches] = useState([]);
  const [searchStats, setSearchStats] = useState(null);
  const fileInputRef = useRef(null);

  // BFF endpoint for authenticated TinEye search
  const BFF_TINEYE_ENDPOINT = `${API_BASE_URL}/bff/tineye-search`;

  const resetState = () => {
    setError('');
    setMatches([]);
    setSearchStats(null);
  };

  const performSearchRequest = async (requestInit, url) => {
    setLoading(true);
    setError('');
    try {
      const response = await fetchWithCsrf(url, requestInit);
      const data = await response.json();
      if (!response.ok || data.code !== 200) {
        const message = (data && data.messages && data.messages.join(' ')) || 'Search failed. Please try a different image.';
        throw new Error(message);
      }
      const foundMatches = (data && data.results && data.results.matches) || [];
      setMatches(foundMatches);
      setSearchStats(data.stats || null);
    } catch (err) {
      setMatches([]);
      setSearchStats(null);
      setError(err.message || 'Unexpected error.');
    } finally {
      setLoading(false);
    }
  };

  const handleUrlSearch = async () => {
    resetState();
    if (!imageUrl || !/^https?:\/\//i.test(imageUrl)) {
      setError('Enter a valid image URL (http/https).');
      return;
    }
    await performSearchRequest(
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // CRITICAL: Send session cookie
        body: JSON.stringify({ imageUrl }),
      },
      BFF_TINEYE_ENDPOINT
    );
  };

  const handleChooseFile = () => {
    if (fileInputRef.current) fileInputRef.current.click();
  };

  const handleFileSearch = async (file) => {
    resetState();
    if (!file) return;
    const formData = new FormData();
    formData.append('image_upload', file);
    await performSearchRequest(
      {
        method: 'POST',
        credentials: 'include', // CRITICAL: Send session cookie
        // Note: do NOT set Content-Type for multipart/form-data; browser will set boundary
        body: formData
      },
      BFF_TINEYE_ENDPOINT
    );
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (file) handleFileSearch(file);
    if (e.target) e.target.value = '';
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      await handleFileSearch(e.dataTransfer.files[0]);
      e.dataTransfer.clearData();
      return;
    }
    const droppedText = e.dataTransfer.getData('text');
    if (droppedText && /^https?:\/\//i.test(droppedText)) {
      setImageUrl(droppedText);
      await handleUrlSearch();
    }
  };

  const handlePaste = async (e) => {
    const items = e.clipboardData && e.clipboardData.items;
    if (items) {
      for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) {
            e.preventDefault();
            await handleFileSearch(file);
            return;
          }
        }
      }
    }
    const text = e.clipboardData && e.clipboardData.getData('text');
    if (text && /^https?:\/\//i.test(text)) {
      e.preventDefault();
      setImageUrl(text);
      await handleUrlSearch();
    }
  };

  return (
    <div onDrop={handleDrop} onDragOver={(e) => e.preventDefault()} onPaste={handlePaste} className="h-full">
      <HubPageLayout>
        <FramedPanel title="Investigating">
          <div className="px-6 pt-8 pb-6">
            <img
              src="/images/detection/investigating-hero.jpeg"
              alt=""
              className="w-full h-[300px] object-cover"
            />
          </div>
        </FramedPanel>

        <div className="mt-8 mb-4">
          <p className="text-black text-lg leading-relaxed">
            Search over <strong>76.5 billion images</strong> and find where images appear online.
            Search is private, and we do not save your search images. A quick reverse search is a
            forensic tool that can point to potential <strong>red flags</strong> — images reused as
            part of a fake profile to make the lie more believable.
          </p>
        </div>

          <div className="pl-12 pr-8 pb-8">
            {/* Search Interface */}
            <div className="w-full mx-auto rounded-lg p-6 bg-white">
              
              {/* Upload Area */}
              <div className="flex flex-col items-center space-y-6">
                
                {/* Upload Icon and Text */}
                <div className="flex items-center space-x-2 text-gray-600">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  <span>Paste, drag, or</span>
                  <button className="text-blue-500 hover:text-blue-600 underline" onClick={handleChooseFile}>
                    upload an image
                  </button>
                  <span>here</span>
                </div>
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileInputChange} />

                {/* Or separator */}
                <div className="text-gray-500 font-medium">
                  or
                </div>

                {/* Search Input */}
                <div className="w-full max-w-2xl relative">
                  <input
                    type="text"
                    placeholder="Search by image url"
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleUrlSearch(); } }}
                  />
                  <button className="absolute right-3 top-1/2 transform -translate-y-1/2 text-blue-500 hover:text-blue-600" onClick={handleUrlSearch}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </button>
                </div>

              </div>
            </div>
          </div>

          

          {/* Status */}
          {(loading || error || (matches && matches.length > 0)) && (
            <div className="text-black mb-8 px-12">
              {loading && (
                <div className="text-gray-600">Searching TinEye sandbox…</div>
              )}
              {!loading && error && (
                <div className="text-red-600">{error}</div>
              )}
              {!loading && !error && searchStats && (
                <div className="text-gray-700 mb-2">
                  <span className="font-semibold">Results:</span>{' '}
                  {typeof searchStats.total_results === 'number' ? searchStats.total_results : matches.length}
                </div>
              )}
            </div>
          )}

          {/* Results */}
          {!loading && !error && matches && matches.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 px-12">
              {matches.map((m, idx) => (
                <div key={`${m.image_url || 'match'}-${idx}`} className="border border-gray-200 rounded-lg p-4 bg-white">
                  {m.image_url && (
                    <div className="mb-3">
                      <img src={m.image_url} alt="Matched" className="w-full h-auto rounded" />
                    </div>
                  )}
                  <div className="text-sm text-black space-y-1">
                    {typeof m.score === 'number' && (
                      <div><span className="font-semibold">Score:</span> {m.score}</div>
                    )}
                    {typeof m.query_match_percent === 'number' && (
                      <div><span className="font-semibold">Query Match %:</span> {m.query_match_percent}</div>
                    )}
                    {m.domain && (
                      <div><span className="font-semibold">Domain:</span> {m.domain}</div>
                    )}
                    {m.overlay && (
                      <div>
                        <a className="text-blue-600 underline" href={`https://api.tineye.com/rest/${m.overlay.replace(/^\/+/, '')}`} target="_blank" rel="noreferrer">View overlay</a>
                      </div>
                    )}
                  </div>
                  {Array.isArray(m.backlinks) && m.backlinks.length > 0 && (
                    <div className="mt-3">
                      <div className="font-semibold text-black mb-1">Backlinks</div>
                      <ul className="list-disc list-inside text-sm text-blue-700">
                        {m.backlinks.slice(0, 5).map((b, i) => (
                          <li key={`${b.backlink || b.url || 'bl'}-${i}`} className="truncate">
                            {b.backlink && (
                              <a className="underline" href={b.backlink} target="_blank" rel="noreferrer">Page</a>
                            )}
                            {b.url && (
                              <>
                                <span className="mx-1 text-gray-500">•</span>
                                <a className="underline" href={b.url} target="_blank" rel="noreferrer">Image</a>
                              </>
                            )}
                            {b.crawl_date && (
                              <span className="mx-1 text-gray-500">• {b.crawl_date}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
      </HubPageLayout>
    </div>
  );
};

export default ImageCheckPage;
